// <pos-qr-settings/> — Owner 產生、下載、列印桌位 QR
// 路徑：#/settings/qr
// see src/designs/owner-qr-settings.html + docs/QR_DEMO_SPRINT.md

import QRCode from 'qrcode';
import { getSupabase } from '../lib/supabase-client.js';
import { listStoreTables, upsertStoreTable, deleteStoreTable } from '../lib/api.js';

const TABLES_KEY_PREFIX = 'pos-qr-tables-';  // localStorage 降級快取（離線/API 失敗時用）

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

class PosQrSettings extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      tenant_slug: null,
      tenant_display_name: null,
      tenant_id: null,
      store_id: null,
      tableNumber: '1',
      tableName: '',
      tableSeats: 4,  // W9 訂位用：每桌容納人數，預設 4
      qrDataUrl: null,
      tables: [],   // [{id, number, name, created_at}] 來自 DB，localStorage 作離線 cache
      loading: true,
      error: null,
      saving: false,
    };
  }

  async connectedCallback() {
    this.render();
    await this.loadTenant();
    await this.loadTables();
    await this.regenerate();
  }

  async loadTenant() {
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('未登入');
      const payload = session.access_token.split('.')[1];
      const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
      const claims = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
      const tenantId = claims.tenant_id;
      if (!tenantId) throw new Error('找不到店家資訊');
      this.state.tenant_id = tenantId;

      const [{ data: tenant, error: tErr }, { data: store, error: sErr }] = await Promise.all([
        supabase.from('tenants').select('slug, display_name').eq('id', tenantId).maybeSingle(),
        supabase.from('stores').select('id').eq('tenant_id', tenantId).eq('is_active', true)
          .order('created_at', { ascending: true }).limit(1).maybeSingle(),
      ]);
      if (tErr) throw tErr;
      if (sErr) throw sErr;
      if (!tenant) throw new Error('找不到店家資料');
      this.state.tenant_slug = tenant.slug;
      this.state.tenant_display_name = tenant.display_name;
      this.state.store_id = store?.id || null;
    } catch (err) {
      this.state.error = err.message_zh || err.message || '讀取店家失敗';
    } finally {
      this.state.loading = false;
    }
  }

  _cacheKey() {
    return TABLES_KEY_PREFIX + (this.state.tenant_slug || 'default');
  }

  _writeCache() {
    try { localStorage.setItem(this._cacheKey(), JSON.stringify(this.state.tables)); } catch { /* quota */ }
  }

  _readCache() {
    try { return JSON.parse(localStorage.getItem(this._cacheKey()) || '[]'); } catch { return []; }
  }

  async loadTables() {
    try {
      const { tables } = await listStoreTables();
      this.state.tables = (tables || []).map(t => ({
        id: t.id,
        number: t.number,
        name: t.name || '',
        seats: t.seats ?? null,
        created_at: t.created_at,
      }));
      this._writeCache();
    } catch (err) {
      console.warn('[qr-settings] loadTables failed, using localStorage cache', err);
      this.state.tables = this._readCache();
    }
  }

  async addCurrentTable() {
    const num = this.state.tableNumber?.trim();
    if (!num) { alert('請輸入桌號'); return; }
    if (!this.state.store_id) { alert('找不到店面，請先建立店家資料'); return; }
    if (this.state.tables.some(t => t.number === num)) {
      alert('此桌號已存在');
      return;
    }
    this.state.saving = true; this.render();
    try {
      const seats = Number.isInteger(this.state.tableSeats) && this.state.tableSeats > 0
        ? this.state.tableSeats
        : undefined;
      const { table } = await upsertStoreTable({
        store_id: this.state.store_id,
        number: num,
        name: this.state.tableName?.trim() || undefined,
        seats,
      });
      this.state.tables.push({
        id: table.id,
        number: table.number,
        name: table.name || '',
        seats: table.seats ?? null,
        created_at: table.created_at,
      });
      this._writeCache();
    } catch (err) {
      alert('新增失敗：' + (err.message_zh || err.message || '未知錯誤'));
    } finally {
      this.state.saving = false;
      this.render();
    }
  }

  async removeTable(num) {
    if (!confirm(`確定要移除桌號 ${num}？（只移除清單，已列印的 QR 仍可用）`)) return;
    const target = this.state.tables.find(t => t.number === num);
    if (!target?.id) {
      // 本機 only（早期沒同步的），直接移除
      this.state.tables = this.state.tables.filter(t => t.number !== num);
      this._writeCache();
      this.render();
      return;
    }
    try {
      await deleteStoreTable(target.id);
      this.state.tables = this.state.tables.filter(t => t.number !== num);
      this._writeCache();
      this.render();
    } catch (err) {
      alert('移除失敗：' + (err.message_zh || err.message || '未知錯誤'));
    }
  }

  async regenerate() {
    const slug = this.state.tenant_slug;
    const num = this.state.tableNumber?.trim();
    if (!slug || !num) return;
    const url = `${location.origin}/#/order/${encodeURIComponent(slug)}/${encodeURIComponent(num)}`;
    try {
      this.state.qrDataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 400,
        color: { dark: '#1A1208', light: '#FFFFFF' },
      });
    } catch (err) {
      this.state.error = 'QR 產生失敗：' + err.message;
    }
    this.render();
  }

  downloadPng() {
    if (!this.state.qrDataUrl) return;
    const a = document.createElement('a');
    a.href = this.state.qrDataUrl;
    a.download = `QR_${this.state.tenant_slug}_table${this.state.tableNumber}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  printQr() {
    const w = window.open('', '_blank');
    if (!w) { alert('瀏覽器擋住彈出視窗，請允許後再試'); return; }
    const { qrDataUrl, tenant_display_name, tableNumber, tableName } = this.state;
    w.document.write(`
      <!doctype html><html><head>
        <title>列印 QR · ${escapeHtml(tenant_display_name)} · 桌號 ${escapeHtml(tableNumber)}</title>
        <style>
          body { margin: 0; padding: 48px; font-family: -apple-system, BlinkMacSystemFont, 'Noto Sans TC', sans-serif; text-align: center; }
          .shop { font-size: 28px; color: #1A1208; letter-spacing: 2px; margin-bottom: 12px; font-weight: 700; }
          .tbl { font-size: 16px; color: #666; margin-bottom: 24px; }
          img { width: 400px; max-width: 85vw; height: auto; border: 2px solid #1A1208; padding: 20px; background: white; }
          .hint { margin-top: 20px; font-size: 14px; color: #888; }
          @media print { body { padding: 20px; } img { border: 1px solid #333; } }
        </style>
      </head><body>
        <div class="shop">${escapeHtml(tenant_display_name)}</div>
        <div class="tbl">桌號 ${escapeHtml(tableNumber)}${tableName ? ' · ' + escapeHtml(tableName) : ''}</div>
        <img src="${qrDataUrl}" alt="QR Code" />
        <div class="hint">請用手機相機掃描點餐</div>
        <script>setTimeout(() => window.print(), 500);<\/script>
      </body></html>
    `);
    w.document.close();
  }

  render() {
    const { loading, error, tenant_slug, tenant_display_name, tableNumber, tableName, qrDataUrl, tables } = this.state;

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>
      <main>
        <aside class="sub-nav">
          <div class="sub-nav__title">設定</div>
          <a class="sub-nav__item" href="#/settings">店家資料</a>
          <a class="sub-nav__item is-active" href="#/settings/qr">桌位 QR Code</a>
          <a class="sub-nav__item" href="#/settings/reservation">訂位設定</a>
          <a class="sub-nav__item" href="#/settings/stations">站點與通知</a>
          <a class="sub-nav__item" href="#/staff">員工</a>
          <a class="sub-nav__item" href="#/settings/ai-keys">AI Keys</a>
        </aside>

        <section class="panel">
          <div class="panel__head">
            <h1 class="panel__title">桌位 QR Code</h1>
            <p class="panel__desc">為每張桌子產生專屬 QR，客人掃描後直接點餐，店員確認後準備</p>
          </div>

          ${loading ? '<div class="banner loading">載入中…</div>' : ''}
          ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}

          ${!loading && !error && tenant_slug ? `
            <div class="qr-card">
              <form class="qr-card__form" id="f">
                <div class="field">
                  <label>桌號</label>
                  <input id="tableNumber" type="text" value="${escapeHtml(tableNumber)}" maxlength="10" />
                  <div class="field__hint">會出現在訂單上，店員辨識用</div>
                </div>

                <div class="field">
                  <label>桌位名稱（選填）</label>
                  <input id="tableName" type="text" value="${escapeHtml(tableName)}" placeholder="例：窗邊吧台" />
                </div>

                <div class="field">
                  <label>容納人數</label>
                  <input id="tableSeats" type="number" min="1" max="99" value="${this.state.tableSeats}" />
                  <div class="field__hint">訂位系統用此數字計算可訂位時段（W9）</div>
                </div>

                <div class="tip">
                  建議列印 10 × 10 cm，貼在桌面中央不阻擋餐具。雷射印表機品質較佳，QR 不易刮花。
                </div>

                <div class="btn-row">
                  <button type="button" class="btn" data-act="print">列印</button>
                  <button type="button" class="btn btn--primary" data-act="download">下載 PNG</button>
                </div>
                <button type="button" class="btn-save" data-act="save">+ 加到已建立桌位清單</button>
              </form>

              <div class="qr-preview">
                ${qrDataUrl
                  ? `<img class="qr-preview__code" src="${qrDataUrl}" alt="QR" />`
                  : '<div class="qr-preview__code">產生中…</div>'}
                <div class="qr-preview__shop">${escapeHtml(tenant_display_name || '')}</div>
                <div class="qr-preview__table">桌號 ${escapeHtml(tableNumber)}${tableName ? ' · ' + escapeHtml(tableName) : ''}</div>
                <div class="qr-preview__hint">請用手機相機掃描</div>
              </div>
            </div>

            <div>
              <h2 class="tables-title">已建立的桌位（本機紀錄）</h2>
              ${tables.length === 0
                ? '<div class="empty">還沒建立任何桌位</div>'
                : `<div class="tables-list">
                    <div class="tables-list__head">
                      <div>桌號</div>
                      <div>名稱</div>
                      <div>人數</div>
                      <div>建立時間</div>
                      <div></div>
                    </div>
                    ${tables.map(t => `
                      <div class="tables-list__row">
                        <div class="tables-list__num">${escapeHtml(t.number)}</div>
                        <div>${escapeHtml(t.name) || '—'}</div>
                        <div>${t.seats ? t.seats + ' 位' : '<span style="color:#c0392b">未設</span>'}</div>
                        <div class="tables-list__date">${new Date(t.created_at).toLocaleString('zh-TW')}</div>
                        <div class="tables-list__actions">
                          <button class="icon-btn" data-act="use" data-num="${escapeHtml(t.number)}" data-name="${escapeHtml(t.name)}">↓ 重新產 QR</button>
                          <button class="icon-btn danger" data-act="rm" data-num="${escapeHtml(t.number)}">刪除</button>
                        </div>
                      </div>
                    `).join('')}
                  </div>`}
            </div>
          ` : ''}
        </section>
      </main>
    `;

    this.wireEvents();
  }

  wireEvents() {
    const root = this.shadowRoot;
    const numEl = root.querySelector('#tableNumber');
    const nameEl = root.querySelector('#tableName');
    if (numEl) numEl.addEventListener('input', async (e) => {
      this.state.tableNumber = e.target.value;
      await this.regenerate();
    });
    if (nameEl) nameEl.addEventListener('input', (e) => {
      this.state.tableName = e.target.value;
      this.render();
    });
    const seatsEl = root.querySelector('#tableSeats');
    if (seatsEl) seatsEl.addEventListener('input', (e) => {
      const n = parseInt(e.target.value, 10);
      this.state.tableSeats = Number.isInteger(n) && n > 0 && n < 100 ? n : 0;
    });

    root.querySelectorAll('[data-act]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const act = btn.dataset.act;
        if (act === 'print') this.printQr();
        else if (act === 'download') this.downloadPng();
        else if (act === 'save') this.addCurrentTable();
        else if (act === 'rm') this.removeTable(btn.dataset.num);
        else if (act === 'use') {
          this.state.tableNumber = btn.dataset.num;
          this.state.tableName = btn.dataset.name || '';
          await this.regenerate();
        }
      });
    });
  }

  styles() {
    return `
      :host { display: block; color: var(--fg-base); background: var(--bg-base); min-height: calc(100vh - 60px); font-family: var(--font-sans); }
      main { display: grid; grid-template-columns: 220px minmax(0, 1fr); gap: var(--s-6); padding: var(--s-6) var(--s-8); }

      .sub-nav { display: grid; gap: var(--s-1); align-content: start; }
      .sub-nav__title { font-size: .75rem; text-transform: uppercase; letter-spacing: 2px; color: var(--fg-dim); padding: 0 var(--s-3) var(--s-2); }
      .sub-nav__item { padding: var(--s-3) var(--s-4); color: var(--fg-muted); text-decoration: none; border-radius: var(--r-md); font-size: .9rem; }
      .sub-nav__item:hover { background: var(--bg-elevated); color: var(--fg-base); }
      .sub-nav__item.is-active { background: var(--bg-elevated); color: var(--brand-primary); border-left: 3px solid var(--brand-primary); padding-left: calc(var(--s-4) - 3px); }

      .panel { display: grid; gap: var(--s-6); align-content: start; }
      .panel__head { display: grid; gap: var(--s-2); }
      .panel__title { font-size: 1.4rem; font-weight: 600; margin: 0; }
      .panel__desc { color: var(--fg-muted); font-size: .9rem; margin: 0; }

      .banner { padding: var(--s-3) var(--s-4); border-radius: var(--r-md); }
      .banner.err { background: rgba(239,68,68,.1); color: var(--danger); }
      .banner.loading { background: rgba(59,130,246,.1); color: var(--fg-muted); }

      .qr-card { display: grid; grid-template-columns: 1fr 1fr; gap: var(--s-8); padding: var(--s-6); background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); }
      .qr-card__form { display: grid; gap: var(--s-4); align-content: start; }
      .field { display: grid; gap: var(--s-2); }
      .field label { font-size: .85rem; color: var(--fg-muted); }
      .field input { background: var(--bg-base); color: var(--fg-base); border: 1px solid var(--border); border-radius: var(--r-md); padding: var(--s-3); font-family: inherit; font-size: .95rem; }
      .field input:focus { outline: none; border-color: var(--brand-primary); }
      .field__hint { font-size: .75rem; color: var(--fg-dim); }

      .tip { padding: var(--s-3) var(--s-4); background: rgba(196, 125, 0, 0.08); border-left: 3px solid var(--brand-accent); color: var(--fg-muted); font-size: .85rem; border-radius: 0 var(--r-md) var(--r-md) 0; }

      .btn-row { display: flex; gap: var(--s-3); margin-top: var(--s-2); }
      .btn, .btn-save { height: 48px; border: 1px solid var(--border); border-radius: var(--r-md); background: var(--bg-surface); color: var(--fg-base); cursor: pointer; font-size: .95rem; font-family: inherit; }
      .btn { flex: 1; }
      .btn:hover, .btn-save:hover { border-color: var(--brand-primary); color: var(--brand-primary); }
      .btn--primary { background: linear-gradient(135deg, var(--brand-primary), var(--brand-accent)); color: var(--fg-on-brand); border-color: transparent; font-weight: 600; }
      .btn--primary:hover { color: var(--fg-on-brand); opacity: .9; }
      .btn-save { margin-top: var(--s-2); padding: 0 var(--s-4); }

      .qr-preview { display: grid; gap: var(--s-2); align-items: center; justify-items: center; background: white; border-radius: var(--r-lg); padding: var(--s-6); color: #1A1208; }
      .qr-preview__code { width: 200px; height: 200px; display: block; image-rendering: pixelated; }
      .qr-preview__shop { font-family: var(--font-display); font-size: 1.1rem; color: #1A1208; letter-spacing: 1px; margin-top: var(--s-3); }
      .qr-preview__table { font-size: .85rem; color: #5a5a70; }
      .qr-preview__hint { font-size: .7rem; color: #888; }

      .tables-title { font-size: 1.05rem; font-weight: 600; margin: 0 0 var(--s-3); }
      .tables-list { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); overflow: hidden; }
      .tables-list__head, .tables-list__row { display: grid; grid-template-columns: 100px 1fr 180px 240px; gap: var(--s-4); padding: var(--s-3) var(--s-5); align-items: center; }
      .tables-list__head { background: var(--bg-surface); color: var(--fg-muted); font-size: .8rem; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid var(--border); }
      .tables-list__row { border-bottom: 1px solid var(--border); }
      .tables-list__row:last-child { border-bottom: 0; }
      .tables-list__num { font-family: var(--font-mono); color: var(--brand-primary); font-size: 1.05rem; }
      .tables-list__date { color: var(--fg-dim); font-size: .85rem; font-family: var(--font-mono); }
      .tables-list__actions { display: flex; gap: var(--s-2); justify-content: flex-end; }
      .icon-btn { padding: var(--s-1) var(--s-3); height: 32px; background: transparent; border: 1px solid var(--border); color: var(--fg-muted); border-radius: var(--r-sm); cursor: pointer; font-size: .8rem; font-family: inherit; }
      .icon-btn:hover { border-color: var(--brand-primary); color: var(--brand-primary); }
      .icon-btn.danger:hover { border-color: var(--danger); color: var(--danger); }

      .empty { padding: var(--s-8); text-align: center; color: var(--fg-dim); background: var(--bg-elevated); border: 1px dashed var(--border); border-radius: var(--r-md); }

      /* 手機：側欄收成內容上方橫向捲動列，單欄堆疊；QR 卡收單欄、桌位表橫向捲動（放最後蓋過桌面基礎規則） */
      @media (max-width: 768px) {
        main { grid-template-columns: minmax(0, 1fr); gap: var(--s-4); padding: var(--s-4); }
        .sub-nav { grid-auto-flow: column; grid-auto-columns: max-content; overflow-x: auto; }
        .sub-nav__item.is-active { border-left: 0; border-bottom: 3px solid var(--brand-primary); padding-left: var(--s-4); }
        .qr-card { grid-template-columns: minmax(0, 1fr); gap: var(--s-5); }
        .tables-list { overflow-x: auto; }
        .tables-list__head, .tables-list__row { min-width: 560px; }
      }
    `;
  }
}

customElements.define('pos-qr-settings', PosQrSettings);
